/**
 * Discovery API Integration Layer
 * Handles all communication with backend discovery endpoints
 */

import * as apiCore from './api.js';

const { API_URL } = apiCore;

export class DiscoveryAPI {
    constructor() {
        this.baseUrl = API_URL + '/discovery';
    }

    /**
     * Generic request handler with error handling
     */
    async _request(url, options = {}) {
        try {
            const response = await apiCore.credentialedFetch(url, options);

            const data = await response.json();

            if (!response.ok) {
                // Preserve the HTTP status + backend error code on the thrown
                // Error so the catch below can surface them (the re-throw already
                // reads error.status / error.code — without this they'd be lost,
                // and callers like the memo handlers couldn't distinguish a 409).
                const err = new Error(data.error || `HTTP ${response.status}`);
                err.status = response.status;
                err.code = data.code || null;
                throw err;
            }

            // Follow backend pattern: { success: true, data: {...} }
            if (data.success === false) {
                throw new Error(data.error || 'Request failed');
            }

            return data;
        } catch (error) {
            console.error('Discovery API Error:', error);
            throw {
                success: false,
                error: error.message,
                code: error.code || 'API_ERROR',
                status: error.status,
                pathId: error.pathId
            };
        }
    }

    /**
     * Get complete questionnaire with all questions for batch completion
     */
    async getFullQuestionnaire(questionnaireId) {
        return this._request(`${this.baseUrl}/user/questionnaires/${questionnaireId}/full`);
    }

    /**
     * Submit all questionnaire responses at once
     * @param {string} questionnaireId - Questionnaire UUID
     * @param {Map} responses - Map of questionId -> responseData
     * @param {Array<string>} deactivatedQuestionIds - IDs of questions deactivated by display rules
     */
    async submitBatchResponses(questionnaireId, responses, deactivatedQuestionIds = []) {
        // Convert Map to array format
        const responsesArray = Array.from(responses.entries()).map(([questionId, responseData]) => ({
            questionId,
            responseData
        }));

        return this._request(`${this.baseUrl}/user/questionnaires/${questionnaireId}/responses/batch`, {
            method: 'POST',
            body: JSON.stringify({
                responses: responsesArray,
                deactivatedQuestionIds
            })
        });
    }

    /**
     * Get all paths with couple-specific progress and recommendations.
     * @param {string} coupleId - Couple UUID
     * @returns {Promise<Object>} Paths with progress and menu counts
     */
    async getPathsWithCoupleProgress(coupleId) {
        return this._request(`${this.baseUrl}/paths/couple/${coupleId}/progress`);
    }

    /**
     * Get all paths with user-specific progress and individual recommendations.
     * For solo users or when no couple context is available.
     * @returns {Promise<Object>} Paths with user progress
     */
    async getPathsWithUserProgress() {
        return this._request(`${this.baseUrl}/paths/user/progress`);
    }

    /**
     * Get path by ID with full metadata (name, description, phase, questionnaires).
     * Uses in-memory ContentRegistry on backend — no DB cost.
     * @param {string} pathId - Path UUID or semantic ID
     * @returns {Promise<Object>} Path data
     */
    async getPathById(pathId) {
        return this._request(`${this.baseUrl}/paths/${pathId}`);
    }

    /**
     * Get current user's partner information
     * @returns {Promise<Object>} Partner user data or null if no partner
     * @returns {Object} returns.data - Partner object with id, firstName,
     *   profilePictureUrl, linkedAt (ISO date the couple was formed), etc.
     */
    async getPartner() {
        return this._request(`${this.baseUrl}/couples/partner`);
    }

    /**
     * Dissolve the authenticated user's couple. Both partners keep their
     * individual data; the couple link is broken so shared progress stops
     * updating.
     * @returns {Promise<Object>} `{ success: true, data: null }` on success.
     */
    async dissolveCouple() {
        return this._request(`${this.baseUrl}/couples/dissolve`, { method: 'POST' });
    }

    /**
     * Create or fetch the user's active partner invitation. Idempotent —
     * returns the existing active invitation if one exists, otherwise mints
     * a new one. The returned data includes the full join URL.
     * @returns {Promise<Object>} `{ success: true, data: { id, token, url, inviteeEmail, createdAt, ... } }`
     */
    async createOrGetInvitation() {
        return this._request(`${this.baseUrl}/couples/invitations`, { method: 'POST' });
    }

    /**
     * Send (or resend) the invitation email to the given address.
     * Backend persists the invitee email on the invitation row.
     * @param {string} invitationId - Invitation UUID
     * @param {string} email - Recipient email address
     * @returns {Promise<Object>} `{ success: true, data: { messageId } }`
     */
    async sendInvitationEmail(invitationId, email) {
        return this._request(
            `${this.baseUrl}/couples/invitations/${invitationId}/send-email`,
            { method: 'POST', body: JSON.stringify({ email }) }
        );
    }

    /**
     * Revoke the user's active invitation. The link stops working and the
     * `inviteeEmail` is wiped along with the row's pending status.
     * @returns {Promise<Object>} `{ success: true, data: null }` on success.
     */
    async revokeInvitation() {
        return this._request(`${this.baseUrl}/couples/invitations/active`, { method: 'DELETE' });
    }

    /**
     * Send a drafted partner memo (draft → sent). Idempotent server-side. On a
     * 409 (draft gone / couple changed) the thrown error carries `status:409`
     * and `code:'MEMO_NOT_AVAILABLE'` so the caller can render "no longer
     * available".
     * @param {string} memoId - PartnerMemo UUID
     * @returns {Promise<Object>} `{ success: true, data: <sent memo> }`
     */
    async sendMemo(memoId) {
        return this._request(`${this.baseUrl}/couples/memos/${encodeURIComponent(memoId)}/send`, {
            method: 'POST'
        });
    }

    /**
     * Discard (hard-delete) the sender's own draft memo.
     * @param {string} memoId - PartnerMemo UUID
     * @returns {Promise<Object>} `{ success: true, data: { id } }`
     */
    async discardMemo(memoId) {
        return this._request(`${this.baseUrl}/couples/memos/${encodeURIComponent(memoId)}`, {
            method: 'DELETE'
        });
    }

    /**
     * List the authenticated user's received (sent, non-revoked) partner memos,
     * newest first. Rendered on the Partner page's Notes → Received list.
     * @returns {Promise<Object>} `{ success: true, data: <memo[]> }`
     */
    async getMemos() {
        return this._request(`${this.baseUrl}/couples/memos`);
    }

    /**
     * List the authenticated user's SENT partner memos, newest first — the Partner
     * Memos page's Sent tab. Each row is the sender-safe five-key whitelist
     * `{ id, content, sentAt, revokedAt, status }` (never `viewedAt`).
     * @returns {Promise<Object>} `{ success: true, data: <memo[]> }`
     */
    async getSentMemos() {
        return this._request(`${this.baseUrl}/couples/memos/sent`);
    }

    /**
     * Retract (soft-revoke) a sent memo. Idempotent server-side; a 409 carries
     * `status:409` + `code:'MEMO_NOT_AVAILABLE'`.
     * @param {string} memoId - PartnerMemo UUID
     * @returns {Promise<Object>} `{ success: true, data: { id, revokedAt } }`
     */
    async revokeMemo(memoId) {
        return this._request(`${this.baseUrl}/couples/memos/${encodeURIComponent(memoId)}/revoke`, {
            method: 'POST'
        });
    }

    /**
     * Mark a received memo viewed, or (pass viewed=false) explicitly un-viewed
     * (the sidebar Recents kebab's "Mark as unread"). Idempotent.
     * @param {string} memoId - PartnerMemo UUID
     * @param {boolean} [viewed=true] - true to mark viewed, false to un-view
     * @returns {Promise<Object>} `{ success: true, data: { count } }`
     */
    async markMemoViewed(memoId, viewed = true) {
        return this._request(`${this.baseUrl}/couples/memos/${encodeURIComponent(memoId)}/viewed`, {
            method: 'POST',
            body: JSON.stringify({ viewed })
        });
    }

    /**
     * Bulk star / unstar received memos (multi-select). Recipient-side marker.
     * @param {string[]} ids - PartnerMemo UUIDs
     * @param {boolean} [starred=true] - true to star, false to unstar
     * @returns {Promise<Object>} `{ success: true, data: { count } }`
     */
    async starMemos(ids, starred = true) {
        return this._request(`${this.baseUrl}/couples/memos/star`, {
            method: 'POST',
            body: JSON.stringify({ ids, starred })
        });
    }

    /**
     * Bulk per-side archive / unarchive (multi-select): moves the notes between
     * the caller's Active and Archived views only.
     * @param {string[]} ids - PartnerMemo UUIDs
     * @param {boolean} [archived=true] - true to archive, false to unarchive
     * @returns {Promise<Object>} `{ success: true, data: { count } }`
     */
    async archiveMemos(ids, archived = true) {
        return this._request(`${this.baseUrl}/couples/memos/archive`, {
            method: 'POST',
            body: JSON.stringify({ ids, archived })
        });
    }

    /**
     * Bulk per-side delete (multi-select): hides the notes from the caller's
     * list only — the partner's copy is untouched.
     * @param {string[]} ids - PartnerMemo UUIDs
     * @returns {Promise<Object>} `{ success: true, data: { count } }`
     */
    async deleteMemos(ids) {
        return this._request(`${this.baseUrl}/couples/memos/delete`, {
            method: 'POST',
            body: JSON.stringify({ ids })
        });
    }

    /**
     * Bulk retract (soft-revoke) sent memos (multi-select). Idempotent.
     * @param {string[]} ids - PartnerMemo UUIDs
     * @returns {Promise<Object>} `{ success: true, data: { count } }`
     */
    async revokeMemosBulk(ids) {
        return this._request(`${this.baseUrl}/couples/memos/revoke-bulk`, {
            method: 'POST',
            body: JSON.stringify({ ids })
        });
    }

    // ═══════════════════════════════════════════════════════════════════════════
    // PARTNER SHARING CONSENT LEDGER (/partner → Sharing registry)
    //
    // All under /api/discovery/couples. Every endpoint returns the standard
    // { success, data } envelope. The read and the two status-shaped mutations
    // return the whole Sharing view model (state machine + masters + share
    // units); the per-unit PATCH returns just that unit's canonical row. The
    // backend 404s (e.g. NO_COUPLE_FOUND) when the caller has no active couple —
    // callers should treat a 404 as "not available", not an error.
    // ═══════════════════════════════════════════════════════════════════════════

    /**
     * Fetch the Sharing view model — the state machine, the masters, and the
     * share-unit ledger the /partner Sharing registry renders.
     * @returns {Promise<Object>} `{ success: true, data: <sharing view model> }`
     * @throws {Object} `{ status: 404 }` when the caller has no active couple —
     *   caller should render the tab as unavailable.
     */
    async getSharingStatus() {
        return this._request(`${this.baseUrl}/couples/sharing-status`);
    }

    /**
     * Set one share unit's consent depth. `off` is always accepted; `full` /
     * `basic` are rejected server-side unless that depth's register verified.
     * @param {string} unitId - Share-unit id (dimension key today)
     * @param {'full'|'basic'|'off'} state
     * @returns {Promise<Object>} `{ success: true, data: <full sharing view model> }` — the
     *   caller extracts the canonical unit row from `data.units` by id
     */
    async setSharingUnit(unitId, state) {
        return this._request(
            `${this.baseUrl}/couples/sharing/unit/${encodeURIComponent(unitId)}`,
            { method: 'PATCH', body: JSON.stringify({ state }) }
        );
    }

    /**
     * Submit the gestalt group's whole disclosure set at once — every unit's
     * depth plus this user's confirm, in one transaction. This is the consent
     * queue's only write: the payload must carry EVERY gestalt dimension key
     * (`off` counts as a decision), and any per-unit rejection names the unit
     * and leaves the ledger untouched.
     * @param {Record<string, 'full'|'basic'|'off'>} unitStates - Keyed by unit id
     * @returns {Promise<Object>} `{ success: true, data: <updated view model> }`
     */
    async submitSharing(unitStates) {
        return this._request(`${this.baseUrl}/couples/sharing/submit`, {
            method: 'POST',
            body: JSON.stringify({ unitStates })
        });
    }

    /**
     * Flip the single Partner Sharing master — the sole pause gate for ALL
     * sharing (the notes feed and assessment sharing alike). OFF tears the
     * shared understanding down and clears this user's confirm while retaining
     * every unit's state; ON re-opens the registry for a fresh confirm.
     * @param {boolean} enabled
     * @returns {Promise<Object>} `{ success: true, data: <updated view model> }`
     */
    async setDiscoveryMaster(enabled) {
        return this._request(`${this.baseUrl}/couples/sharing/master`, {
            method: 'PATCH',
            body: JSON.stringify({ discovery: enabled })
        });
    }

    /**
     * Dismiss the one-time W9 first-feed notice (shown before shared notes first inform the
     * partner's guidance).
     * @returns {Promise<Object>} `{ success: true, data: { memoFeedNoticeSeen: true } }`
     */
    async dismissMemoFeedNotice() {
        return this._request(`${this.baseUrl}/couples/sharing/memo-feed-notice/dismiss`, {
            method: 'POST'
        });
    }

    /**
     * Get core profile progress for "Your Progress" tab
     * @returns {Promise<Object>} Progress groups with percentages and section counts
     */
    async getCoreProgress() {
        return this._request(`${this.baseUrl}/progress/core`);
    }

    /**
     * Get whole-brain progress (per-region buckets {t1,t2} + per-topic %) for the
     * understanding card and the /discovery brain visual.
     * @returns {Promise<Object>} { data: { regions, topics } }
     */
    async getBrainProgress() {
        return this._request(`${this.baseUrl}/progress/brain`);
    }

    /**
     * Get orchestrator progress for a specific path.
     * @param {string} pathId - Path ID
     * @returns {Promise<Object>} Progress with flow progress, section buckets, and pipeline status
     */
    async getPathProgress(pathId) {
        return this._request(`${this.baseUrl}/paths/${pathId}/progress`);
    }

    /**
     * Queue a content plan for execution (e.g., Haiku intro messages).
     * @param {string} sessionId - Active session UUID
     * @param {Array<Object>} contentPlan - Content plan items
     * @returns {Promise<Object>} Response with streamUrl and jobId
     */
    async queueContentPlan(sessionId, contentPlan) {
        return this._request(`${API_URL}/chat/content-plan`, {
            method: 'POST',
            body: JSON.stringify({ sessionId, contentPlan })
        });
    }

    // ═══════════════════════════════════════════════════════════════════════════
    // GUIDED SUBCATEGORY CHOICE
    // ═══════════════════════════════════════════════════════════════════════════

    /**
     * Persist the user's guided topic selection across sessions.
     * @param {string} topicId - Topic ID to persist
     * @returns {Promise<Object>} Response
     */
    async selectTopic(topicId) {
        return this._request(`${this.baseUrl}/paths/guided/select-topic`, {
            method: 'PATCH',
            body: JSON.stringify({ topicId })
        });
    }

    /**
     * Clear the user's guided topic selection.
     * @returns {Promise<Object>} Response
     */
    async clearTopicSelection() {
        return this._request(`${this.baseUrl}/paths/guided/select-topic`, {
            method: 'PATCH',
            body: JSON.stringify({ topicId: null })
        });
    }

    /**
     * Get the next recommended topic from the ranking engine.
     * @param {string[]} [exclude=[]] - Topic IDs to exclude
     * @returns {Promise<Object>} Topic result or { journeyComplete: true }
     */
    async getNextTopic(exclude = []) {
        const params = exclude.length ? `?exclude=${exclude.join(',')}` : '';
        return this._request(`${this.baseUrl}/paths/guided/next${params}`);
    }

    // ═══════════════════════════════════════════════════════════════════════════
    // ORCHESTRATED PATH ENDPOINTS
    // ═══════════════════════════════════════════════════════════════════════════

    /**
     * Bundled authored-path next endpoint. Records an optional action and
     * returns the next step in one round trip.
     *
     * Response envelope type strings are load-bearing in the frontend and
     * therefore preserved here.
     *
     * @param {string} pathId
     * @param {string} [action]
     * @param {Object} [actionData={}]
     * @returns {Promise<Object>}
     */
    async postPathNext(pathId, action, actionData = {}) {
        const body = action ? { action, ...actionData } : { ...actionData };
        return this._request(`${this.baseUrl}/paths/${encodeURIComponent(pathId)}/next`, {
            method: 'POST',
            body: JSON.stringify(body)
        });
    }

    /**
     * Bundled topic next endpoint. Records an optional action and returns the
     * next step in one round trip. Used by guided/topic entry modes whose
     * scopeId is the topicId itself (so the path POST endpoint cannot serve
     * them).
     *
     * @param {string} topicId
     * @param {string} [action]
     * @param {Object} [actionData={}]
     * @returns {Promise<Object>}
     */
    async postTopicNext(topicId, action, actionData = {}) {
        const body = action ? { action, ...actionData } : { ...actionData };
        return this._request(`${this.baseUrl}/topics/${encodeURIComponent(topicId)}/next`, {
            method: 'POST',
            body: JSON.stringify(body)
        });
    }

    // ═══════════════════════════════════════════════════════════════════════════
    // ENTRY ROUTES
    // ═══════════════════════════════════════════════════════════════════════════

    /**
     * Guided mode: foundation gate + ranker. Returns `{ topicId, label, ... }`
     * for a regular topic, `{ type: 'foundation_required', ... }` when Foundation
     * is incomplete, or `{ journeyComplete: true }` when everything is done.
     * @param {string[]} [exclude=[]] - Topic IDs to exclude
     * @param {Object} [opts]
     * @param {string} [opts.excludeL3Section] - Section code to exclude from L3
     *   ranking. Used after an L3 closes so the async pipeline has a window to
     *   write before that section is re-considered.
     */
    async fetchNextForGuided(exclude = [], { excludeL3Section } = {}) {
        const parts = [];
        if (exclude.length) parts.push(`exclude=${exclude.join(',')}`);
        if (excludeL3Section) parts.push(`excludeL3Section=${encodeURIComponent(excludeL3Section)}`);
        const params = parts.length ? `?${parts.join('&')}` : '';
        return this._request(`${this.baseUrl}/paths/guided/next${params}`);
    }

    /**
     * Read-only preview of the guided recommendation, for display surfaces
     * (the /discovery Progress tab's "Recommended next" tile).
     *
     * Deliberately a separate wrapper rather than a `peek` option on
     * {@link getNextTopic} / {@link fetchNextForGuided}: those two are real
     * guided *entry* and must keep clearing a stale `activeGuidedTopic`.
     * Under `?peek=1` the route skips both `activeGuidedTopic` writes and the
     * `prepareL3Session` call (which emits conversation-start telemetry), and
     * returns a trimmed `{ type: 'l3_session', sectionCode, sectionName }` for
     * the L3 branch. The payload is display-only — it is never sufficient to
     * start a session with.
     *
     * @returns {Promise<Object>} `{ type: 'foundation_required', pathId }`, a
     *   ranked topic result, a trimmed `l3_session` descriptor, or
     *   `{ journeyComplete: true }`.
     */
    async peekNextForGuided() {
        return this._request(`${this.baseUrl}/paths/guided/next?peek=1`);
    }

    // ═══════════════════════════════════════════════════════════════════════════
    // L3 DEEP DIVE COMPLETION
    // ═══════════════════════════════════════════════════════════════════════════

    /**
     * Signal that an L3 deep-dive session has ended. Enqueues the discovery
     * pipeline so the narrative generator regenerates the MemoryInsight.
     * @param {string} sectionCode
     * @param {string} sessionId
     * @returns {Promise<Object>} { sectionCode, sectionName, understanding, processed }
     */
    async completeL3Session(sectionCode, sessionId) {
        return this._request(`${this.baseUrl}/l3/completed`, {
            method: 'POST',
            body: JSON.stringify({ sectionCode, sessionId })
        });
    }
}
